import { GridApi, ProcessCellForExportParams } from "ag-grid-enterprise";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

// Human-readable risk labels shared between the Before Risk / After Risk
// columns. Data is stored as HIGH/MEDIUM/LOW/DELETED enum values; the on-
// screen cell renderer maps them for display, but Excel/CSV exports see the
// raw value unless we transform here.
const RISK_LABELS: Record<string, string> = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  DELETED: "Deleted"
};

// Column ids that contain activity/milestone dates exported as virtual
// columns. AG Grid calls processCellCallback with raw values for these
// (since the columns are hidden + have no valueFormatter on export).
const DATE_COL_IDS = new Set([
  "old_activity_date",
  "new_activity_date",
  "final_deadline"
]);

function formatDelta(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const num = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(num)) return "";
  const unit = Math.abs(num) === 1 ? "day" : "days";
  return num > 0 ? `+${num} ${unit}` : `${num} ${unit}`;
}

function formatDate(raw: unknown): string {
  if (!raw) return "";
  const out = DateUtils.format(String(raw), DATE_FORMAT_MMDDYYYY);
  return out || "";
}

/**
 * processCellCallback for AG Grid's exportDataAsExcel — maps risk enum values
 * to readable labels, formats delta days, and forces MM/DD/YYYY on date
 * columns (including the hidden Old/New Activity Date columns that don't
 * have a valueFormatter on the grid side).
 */
export function scheduleImpactProcessCell(
  params: ProcessCellForExportParams<any>
): string {
  const colId = params.column.getColId();
  const isDeleted = params.node?.data?.risk_level === "DELETED";

  if (isDeleted && (colId === "risk_after" || colId === "final_deadline")) {
    return "";
  }

  if (colId === "risk_before" || colId === "risk_after") {
    if (params.value == null || params.value === "") return "";
    return RISK_LABELS[params.value] || String(params.value);
  }

  if (colId === "delta") {
    return formatDelta(params.value);
  }

  if (DATE_COL_IDS.has(colId)) {
    return formatDate(params.value);
  }

  const formatted = params.formatValue(params.value);
  if (formatted != null && formatted !== "") return String(formatted);
  return params.value == null ? "" : String(params.value);
}

export const SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_SUBMITTAL: string[] = [
  "submittal_sequence_id",
  "title",
  "spec_section",
  "spec_name",
  "responsible_contractor",
  "gc_reviewer",
  "status",
  "linked_activity",
  "old_activity_date",
  "new_activity_date",
  "delta",
  "change_in_float",
  "risk_before",
  "risk_after",
  "risk_assessment",
  "final_deadline"
];

export const SCHEDULE_IMPACT_EXPORT_COLUMN_KEYS_MATERIAL: string[] = [
  "material_sequence_id",
  "title",
  "spec_section",
  "spec_name",
  "responsible_contractor",
  "gc_representative",
  "status",
  "linked_activity",
  "old_activity_date",
  "new_activity_date",
  "delta",
  "change_in_float",
  "risk_before",
  "risk_after",
  "risk_assessment",
  "final_deadline"
];

// Columns that must always go into the export regardless of the user's
// column-visibility selection in the grid tool panel:
// - ID is locked-visible in the panel (mandatory).
// - The 4 date/risk fields are not in the panel at all and are exported
//   for downstream processing.
export const ALWAYS_EXPORT_KEYS_SUBMITTAL: string[] = [
  "submittal_sequence_id",
  "old_activity_date",
  "new_activity_date",
  "delta",
  "risk_assessment"
];

export const ALWAYS_EXPORT_KEYS_MATERIAL: string[] = [
  "material_sequence_id",
  "old_activity_date",
  "new_activity_date",
  "delta",
  "risk_assessment"
];

/**
 * Returns the canonical export column keys filtered by the user's current
 * grid column-visibility state. Columns in `alwaysExport` are forced in
 * regardless of the panel toggle; toggleable columns are kept only when
 * AG Grid reports them as visible. Order is preserved from `allKeys`.
 */
export function getVisibleExportColumnKeys(
  api: GridApi,
  allKeys: string[],
  alwaysExport: string[]
): string[] {
  const alwaysSet = new Set(alwaysExport);
  return allKeys.filter(
    (k) => alwaysSet.has(k) || api.getColumn(k)?.isVisible() === true
  );
}

// SI column id → rename key in the per-feature `field_mapping` config the
// settings module saves. Only columns that have a semantic equivalent in
// the Submittal/Material log rename list are included here. SI-specific
// columns (delta, change_in_float, before/after risk, risk_assessment,
// old/new activity dates, spec_name) are intentionally absent — they
// stay as their static labels.
export const SI_SUBMITTAL_RENAME_KEY: Record<string, string> = {
  submittal_sequence_id: "submittal_id",
  title: "title",
  spec_section: "spec_no",
  responsible_contractor: "responsible_contractor",
  gc_reviewer: "gc_reviewer",
  status: "workflow_status",
  linked_activity: "linked_activities",
  final_deadline: "final_deadline_date"
};

export const SI_MATERIAL_RENAME_KEY: Record<string, string> = {
  material_sequence_id: "material_id",
  title: "name",
  spec_section: "spec_section_no",
  responsible_contractor: "trade_partner",
  gc_representative: "gc_representative",
  status: "status",
  linked_activity: "linked_activities",
  final_deadline: "final_deadline_date"
};

/**
 * Resolves the user-facing label for an SI column: returns the renamed
 * caption from settings if one exists, otherwise the static default.
 */
export function resolveSiHeader(
  headerMap: Record<string, string> | undefined | null,
  defaultLabel: string,
  renameKey: string | undefined
): string {
  if (!renameKey) return defaultLabel;
  return headerMap?.[renameKey] || defaultLabel;
}

// Matches filename chars that Excel / OS file systems can't handle.
const FILENAME_BAD_CHARS = /[\\/:*?"<>|]/g;

export function buildExportFileName(
  prefix: string,
  projectName?: string
): string {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const parts = [prefix];
  if (projectName) {
    parts.push(projectName.replace(FILENAME_BAD_CHARS, "").trim() || "project");
  }
  parts.push(`${yyyy}-${mm}-${dd}`);
  return parts.filter(Boolean).join("_");
}
